import * as React from 'react';
import cx from 'classnames';

import { getPropClassesFromInstance } from 'lib/getPropClasses';
import Button from 'components/Button';
// @ts-ignore
import formatMediaTime from 'utils/formatMediaTime';
import Glyph from 'components/Glyph';
import Slider from 'components/Slider';
// @ts-ignore
import withWidth from 'lib/withWidth';

import { GlyphName } from '@remind/r101-glyphs';

import './styles/MediaPlayer.scss';

// use via Audio.jsx or Video.jsx

type Props = {
  downloadable?: boolean;
  fullscreen?: boolean;
  playerId: string;
  src: string;
  theme: 'light' | 'dark';
  className?: string;
  width: number;
};

type State = {
  playing: boolean;
  wasPlaying: boolean;
  currentTime: number;
  totalTime: number;
  muted: boolean;
  volume: number;
};

class MediaPlayer extends React.Component<Props, State> {
  static propClasses = {
    theme: {
      light: 'MediaPlayer--light',
      dark: 'MediaPlayer--dark',
    },
    fullscreen: 'MediaPlayer--fullscreenEnabled',
  };

  static SMALL = 440;

  static defaultProps = {
    fullscreen: false,
    downloadable: true,
  };

  state = {
    playing: false,
    wasPlaying: false,
    currentTime: 0,
    totalTime: 0,
    muted: false,
    volume: 1.0,
  };

  componentDidMount() {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('componentDidMount');
      return;
    }

    if (player.tagName === 'VIDEO') {
      player.addEventListener('click', this.togglePlayback, false);
    }
    player.addEventListener('durationchange', this.handleDurationChange, false);
    player.addEventListener('timeupdate', this.handleTimeUpdate, false);
    player.addEventListener('volumechange', this.handleVolumeChange, false);
    player.addEventListener('play', this.handlePlay, false);
    player.addEventListener('pause', this.handlePause, false);
    player.addEventListener('ended', this.handleEnded, false);
  }

  componentWillUnmount() {
    const player = this.getPlayer();

    if (!player) {
      // if this component lives inside a modal, that modal may unmount before
      // this component has a chance to properly run through this method
      return;
    }

    if (player.tagName === 'VIDEO') {
      player.removeEventListener('click', this.togglePlayback, false);
    }
    player.removeEventListener('durationchange', this.handleDurationChange, false);
    player.removeEventListener('timeupdate', this.handleTimeUpdate, false);
    player.removeEventListener('volumechange', this.handleVolumeChange, false);
    player.removeEventListener('play', this.handlePlay, false);
    player.removeEventListener('pause', this.handlePause, false);
    player.removeEventListener('ended', this.handleEnded, false);
  }

  handleEmptyPlayer(method: string) {
    console.error(`MediaPlayer ${method} method - getPlayer returned a null value`);
  }

  handleAfterChangePlayback = () => {
    if (this.state.wasPlaying) {
      this.play();
    }
  };

  handleBeforeChangePlayback = () => {
    this.setState(({ playing }) => ({ wasPlaying: playing, playing: false }));
    this.pause();
  };

  handleChangePlayback = (value: number) => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('handleChangePlayback');
      return;
    }

    player.currentTime = value;
  };

  handleChangeVolume = (value: number) => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('handleChangeVolume');
      return;
    }

    player.volume = value;
  };

  handleDurationChange = () => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('handleDurationChange');
      return;
    }

    this.setState({ totalTime: player.duration });
  };

  handleEnded = () => {
    this.setState({ playing: false });
  };

  handlePause = () => {
    if (this.state.playing) {
      this.setState({ playing: false });
    }
  };

  handlePlay = () => {
    this.setState({ playing: true });
  };

  handleTimeUpdate = () => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('handleTimeUpdate');
      return;
    }

    this.setState({ currentTime: player.currentTime });
  };

  handleVolumeChange: EventListenerOrEventListenerObject = event => {
    if (!event.target) {
      return;
    }

    if (this.state.muted === (event.target as HTMLMediaElement).muted) {
      // Volume was changed
      if ((event.target as HTMLMediaElement).volume === 0.0) {
        this.mute();
      } else {
        this.unmute();
      }
      this.setState({ volume: (event.target as HTMLMediaElement).volume });
    } else {
      // Mute was changed
      this.setState({ muted: (event.target as HTMLMediaElement).muted });
    }
  };

  getButtonColor = () => {
    return this.props.theme === 'dark' ? 'white' : 'brand';
  };

  getPlayer = (): HTMLMediaElement | null => {
    return document.getElementById(this.props.playerId) as HTMLMediaElement;
  };

  makeFullscreen = () => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('makeFullscreen');
      return;
    }

    player.requestFullscreen();
  };

  mute = () => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('mute');
      return;
    }

    player.muted = true;
  };

  pause = () => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('pause');
      return;
    }

    player.pause();
  };

  play = () => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('play');
      return;
    }

    player.play();
  };

  toggleMute = () => {
    if (this.state.muted) {
      this.unmute();
    } else {
      this.mute();
    }
  };

  togglePlayback = () => {
    if (this.state.playing) {
      this.pause();
    } else {
      this.play();
    }
  };

  unmute = () => {
    const player = this.getPlayer();

    if (!player) {
      this.handleEmptyPlayer('unmute');
      return;
    }

    player.muted = false;
  };

  renderDownload = () => {
    if (!this.props.downloadable) {
      return null;
    }

    return (
      <Button
        aria-label="Download"
        download
        href={this.props.src}
        className="MediaPlayer-download"
        level="icon"
        color={this.getButtonColor()}
        tooltip="Download"
        tooltipPlacement="bottom"
        eventElementName="MediaPlayer-download">
        <Glyph name="download" size="medium" solo />
      </Button>
    );
  };

  renderFullscreen = () => {
    if (!this.props.fullscreen) {
      return null;
    }

    return (
      <Button
        aria-label="Full screen"
        className="MediaPlayer-fullscreen"
        level="icon"
        color={this.getButtonColor()}
        onClick={this.makeFullscreen}
        tooltip="Fullscreen"
        tooltipPlacement="bottom"
        eventElementName="MediaPlayer-fullscreen">
        <Glyph name="fullscreen" size="medium" solo />
      </Button>
    );
  };

  renderPlayback = () => {
    const glyphName = this.state.playing ? 'pause' : 'play';

    return (
      <Button
        aria-label={this.state.playing ? 'Pause' : 'Play'}
        className="MediaPlayer-playback"
        level="icon"
        color={this.getButtonColor()}
        onClick={this.togglePlayback}
        eventElementName="MediaPlayer-playback">
        <Glyph name={glyphName} size="large" solo />
      </Button>
    );
  };

  renderPlaybackSlider = () => {
    return (
      <div
        className={cx({
          'MediaPlayer-playbackSlider': true,
          'MediaPlayer-playbackSliderSmall': this.props.width < MediaPlayer.SMALL,
        })}>
        <Slider
          min={0}
          max={this.state.totalTime}
          step={0.1}
          value={this.state.currentTime}
          onBeforeChange={this.handleBeforeChangePlayback}
          onChange={this.handleChangePlayback}
          onAfterChange={this.handleAfterChangePlayback}
          theme={this.props.theme}
          size="thick"
        />
      </div>
    );
  };

  renderTime = () => {
    const showHours = this.state.totalTime / 3600 > 0;

    if (this.props.width < MediaPlayer.SMALL) {
      return this.renderTimeSmall(showHours);
    }

    return this.renderTimeNormal(showHours);
  };

  renderTimeNormal = (showHours: boolean) => {
    return (
      <div className="MediaPlayer-time">
        <div>
          <div className="MediaPlayer-currentTime">
            {formatMediaTime(this.state.currentTime, showHours)}
          </div>
          <div className="MediaPlayer-timeDivider">/</div>
          <div className="MediaPlayer-totalTime">
            {formatMediaTime(this.state.totalTime, showHours)}
          </div>
        </div>
      </div>
    );
  };

  renderTimeSmall = (showHours: boolean) => {
    return (
      <div className="MediaPlayer-time MediaPlayer-timeSmall">
        <div className="MediaPlayer-currentTime">
          {formatMediaTime(this.state.currentTime, showHours)}
        </div>
      </div>
    );
  };

  renderVolume = () => {
    if (this.props.width < MediaPlayer.SMALL) {
      return null;
    }

    let tooltipMessage = 'Mute';
    let volume;
    if (this.state.muted || this.state.volume === 0.0) {
      tooltipMessage = 'Unmute';
      volume = 'mute';
    } else if (this.state.volume < 0.33) {
      volume = '1';
    } else if (this.state.volume < 0.67) {
      volume = '2';
    } else {
      volume = '3';
    }

    return (
      <Button
        aria-label="Adjust volume"
        className="MediaPlayer-volume"
        level="icon"
        color={this.getButtonColor()}
        onClick={this.toggleMute}
        tooltip={tooltipMessage}
        tooltipPlacement="bottom"
        eventElementName="MediaPlayer-volume">
        <Glyph name={`audio-level-${volume}` as GlyphName} size="large" solo />
      </Button>
    );
  };

  renderVolumeSlider = () => {
    if (this.props.width < MediaPlayer.SMALL) {
      return null;
    }

    return (
      <div className="MediaPlayer-volumeSlider">
        <Slider
          min={0.0}
          max={1.0}
          step={0.1}
          value={this.state.muted ? 0.0 : this.state.volume}
          onChange={this.handleChangeVolume}
          theme={this.props.theme}
          size="thin"
        />
      </div>
    );
  };

  render() {
    return (
      <div className={cx('MediaPlayer', getPropClassesFromInstance(this), this.props.className)}>
        {this.renderPlayback()}
        {this.renderPlaybackSlider()}
        {this.renderTime()}
        {this.renderVolume()}
        {this.renderVolumeSlider()}
        {this.renderDownload()}
        {this.renderFullscreen()}
      </div>
    );
  }
}

export { MediaPlayer };
export default withWidth(MediaPlayer);
